import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Toast from '../../Shared/Toast';
import styles from './Ranking.module.css';

interface MercenaryInfo {
  id: number;
  name: string;
  level: number;
  race: string;
  title?: string | null;
  isBot?: boolean;
  deathDate?: string | null;
  killedBy?: string | null;
}

interface StatEntry {
  mercenaryId: number;
  mercenary: MercenaryInfo | null;
  [key: string]: any;
}

interface ReputationEntry {
  id: number;
  name: string;
  reputation: number;
  title?: string | null;
  deathDate?: string | null;
  killedBy?: string | null;
}

interface CombatLeaderboardsData {
  seasonActive?: boolean;
  maxDamageAttack: StatEntry[];
  maxDamageMatch: StatEntry[];
  maxDodges: StatEntry[];
  maxBlocks: StatEntry[];
  tournamentWins: StatEntry[];
  mostCriticalHits: StatEntry[];
  mostParries: StatEntry[];
  mostKills: StatEntry[];
  mostHospitalizations: StatEntry[];
  mostInfirmaryVisits: StatEntry[];
  highestReputation: ReputationEntry[];
  lowestReputation: ReputationEntry[];
}

const CombatLeaderboards: React.FC = () => {
  const [data, setData] = useState<CombatLeaderboardsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Function to pad entries to exactly 5 items with placeholders
  const padEntries = (entries: StatEntry[], statKey: string): StatEntry[] => {
    if (!entries) return [];
    const paddedEntries = [...entries];
    while (paddedEntries.length < 5) {
      paddedEntries.push({
        mercenaryId: -1,
        mercenary: null,
        [statKey]: 0
      });
    }
    return paddedEntries;
  };

  // Function to pad reputation entries to exactly 5 items with placeholders
  const padReputationEntries = (entries: ReputationEntry[]): ReputationEntry[] => {
    const paddedEntries = [...entries];
    while (paddedEntries.length < 5) {
      paddedEntries.push({
        id: -1,
        name: '---',
        reputation: 0
      });
    }
    return paddedEntries;
  };

  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const hasDeadMarker = (entry?: { deathDate?: string | null; name?: string | null }) => (
    !!entry?.deathDate || /\s+\(Dead\)$/i.test(entry?.name || '')
  );

  const stripDeadSuffix = (name?: string | null) => (name || '').replace(/\s+\(Dead\)$/i, '');

  const getMercenaryNameClassName = (title?: string | null, isDead?: boolean) => (
    [
      styles.mercenaryName,
      title === 'Legend' ? styles.legendName : '',
      isDead ? styles.deadMercenaryName : '',
    ].filter(Boolean).join(' ')
  );

  useEffect(() => {
    fetchCombatLeaderboards();
  }, []);

  const fetchCombatLeaderboards = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/mercenary/combat-leaderboards', {
        withCredentials: true,
      });

      setData(response.data);
    } catch (error) {
      console.error('Error fetching combat leaderboards:', error);
      displayToast('Failed to load combat leaderboards. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderStatSection = (title: string, description: string, entries: StatEntry[], statKey: string) => {
    const paddedEntries = padEntries(entries, statKey);

    return (
      <div className={styles.section}>
        <h3 title={description}>{title}</h3>
        <p className={styles.sectionDescription}>{description}</p>
        <div className={styles.leaderboardList}>
          {paddedEntries.map((entry, index) => (
            <div key={`${entry.mercenaryId}-${index}`} className={styles.leaderboardEntry}>
              <span className={styles.rank}>#{index + 1}</span>
              <div className={styles.mercenaryDetails}>
                {entry.mercenary ? (
                  <a
                    href={`/mercenary/${entry.mercenary.id}`}
                    className={getMercenaryNameClassName(entry.mercenary.title, hasDeadMarker(entry.mercenary))}
                  >
                    {stripDeadSuffix(entry.mercenary.name)}
                    {entry.mercenary.isBot && <span className={styles.botBadge}>B</span>}
                  </a>
                ) : (
                  <span className={styles.placeholderName}>---</span>
                )}
                {entry.mercenary && (
                  <span className={styles.mercenaryInfo}>
                    Level {entry.mercenary.level} {entry.mercenary.race}
                  </span>
                )}
              </div>
              <span className={styles.statValue}>
                {entry[statKey] || '---'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderReputationSection = (title: string, description: string, entries: ReputationEntry[], isPositive: boolean) => {
    const paddedEntries = padReputationEntries(entries);

    return (
      <div className={styles.section}>
        <h3 title={description}>{title}</h3>
        <p className={styles.sectionDescription}>{description}</p>
        <div className={styles.leaderboardList}>
          {paddedEntries.map((entry, index) => (
            <div key={`${entry.id}-${index}`} className={styles.leaderboardEntry}>
              <span className={styles.rank}>#{index + 1}</span>
              <div className={styles.mercenaryDetails}>
                {entry.id !== -1 ? (
                  <a
                    href={`/mercenary/${entry.id}`}
                    className={getMercenaryNameClassName(entry.title, hasDeadMarker(entry))}
                  >
                    {stripDeadSuffix(entry.name)}
                  </a>
                ) : (
                  <span className={styles.placeholderName}>---</span>
                )}
              </div>
              <span className={`${styles.statValue} ${isPositive ? styles.positive : styles.negative}`}>
                {entry.id !== -1 ? entry.reputation : '---'}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <h2>Wall of Legends</h2>
        <div className={styles.loading}>Loading combat statistics...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.container}>
        <h2>Wall of Legends</h2>
        <div className={styles.error}>Failed to load combat statistics.</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2>Wall of Legends</h2>
      <p className={styles.description}>
        Discover the most skilled mercenaries in Njordheim through their combat achievements.
      </p>
      <p className={styles.description}>
        Only PvP battles are considered for these leaderboards.
      </p>

      <div className={styles.leaderboardGrid}>
        {renderStatSection(
          'Worldbreaker',
          'Highest damage dealt in a single attack',
          data.maxDamageAttack,
          'highestDamageDealt'
        )}

        {renderStatSection(
          'Unending Storm',
          'Most total damage dealt in one battle',
          data.maxDamageMatch,
          'mostDamageDone'
        )}

        {renderStatSection(
          'Untouchable',
          'Most dodges in one battle',
          data.maxDodges,
          'mostDodgedAttacks'
        )}

        {renderStatSection(
          'Bladedancer',
          'Most parries in one battle',
          data.mostParries,
          'mostParries'
        )}

        {renderStatSection(
          'Iron Wall',
          'Most blocks in one battle',
          data.maxBlocks,
          'mostBlockedAttacks'
        )}

        {renderStatSection(
          'Crownbearer',
          'Most tournament first-place finishes',
          data.tournamentWins,
          'tournamentFirstPlace'
        )}

        {renderStatSection(
          'Heartpiercer',
          'Most critical hits landed in one battle',
          data.mostCriticalHits,
          'mostCriticalHits'
        )}

        {renderStatSection(
          'Crown of Bones',
          'Most kills across the season',
          data.mostKills,
          'kills'
        )}

        {renderStatSection(
          'Bonebreaker',
          'Most hospitalizations caused this season',
          data.mostHospitalizations,
          'hospitalizations'
        )}

        {data.mostInfirmaryVisits && renderStatSection(
          'Punching Bag',
          'Most hospitalizations received this season',
          data.mostInfirmaryVisits,
          'hospitalizationCount'
        )}

        {renderReputationSection(
          'Radiant One',
          'Highest reputation — the most revered mercenary',
          data.highestReputation,
          true
        )}

        {renderReputationSection(
          'The Dreaded',
          'Lowest reputation — the most infamous mercenary',
          data.lowestReputation,
          false
        )}
      </div>

      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage(null)}
        />
      )}
    </div>
  );
};

export default CombatLeaderboards;
